import { createContext, FC, ReactNode, useState, useEffect, useCallback } from "react";
import { useHttpUtils } from "../Utilities/useHttpUtils";

export type Privileges = {
    admin?: boolean,
    supervisor?: boolean,
    canProcess?: boolean,
    canReview?: boolean,
    canGrade?: boolean,
    canConfigure?: boolean,
    setBusinessUnitCode?: (code: string) => void,
    businessUnitsUserCanProcess?: string[],
}

interface BusinessUnitPrivileges {
    canProcess: boolean;
    canReview: boolean;
    canGrade: boolean;
    supervisor: boolean;
}

const PrivilegeContext = createContext<Privileges>({});

export const PrivilegeProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [businessUnitCode, setBusinessUnitCode] = useState<string>();
    const [admin, setAdmin] = useState<boolean>();
    const [supervisor, setSupervisor] = useState<boolean>();
    const [canProcess, setCanProcess] = useState<boolean>();
    const [canReview, setCanReview] = useState<boolean>();
    const [canGrade, setCanGrade] = useState<boolean>();
    const [canConfigure, setCanConfigure] = useState<boolean>();
    const [businessUnitsUserCanProcess, setBusinessUnitsUserCanProcess] = useState<string[]>([])

    const getAuthenticatedAccountPrivilege = useCallback(async (privilegeName: string, businessUnitCode: string | undefined = undefined) => {
        let accessGranted = false;

        let url = `api/v1/user/checkPrivilege?privilegeName=${privilegeName}`;
        if (businessUnitCode) {
            url += `&businessUnitCode=${businessUnitCode}`;
        }

        await fetchDataAuthenticated(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to check privilege ${privilegeName} for business unit ${businessUnitCode}`);
                }
                return response.json();
            })
            .then(data => {
                accessGranted = !!data;
            })
            .catch(error => console.error(error.message));

        return accessGranted;
    }, [fetchDataAuthenticated]);

    const getAuthenticatedAccountBusinessUnitsForPrivilege = useCallback(async (privilegeName: string) => {
        let businessUnitsAllowed: string[] = [];

        let url = `api/v1/user/businessUnitsForPrivilege?privilegeName=${privilegeName}`;

        await fetchDataAuthenticated(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to check business units for privilege ${privilegeName}`);
                }
                return response.json();
            })
            .then(data => {
                businessUnitsAllowed = data;
            })
            .catch(error => console.error(error.message));

        return businessUnitsAllowed;
    }, [fetchDataAuthenticated]);

    const getPrivilegesForBusinessUnit = useCallback((businessUnitCode: string) => {
        fetchDataAuthenticated(`api/v1/user/privilegesForBusinessUnit?businessUnitCode=${encodeURIComponent(businessUnitCode)}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to get privileges for business unit ${businessUnitCode}`);
                }
                return response.json();
            })
            .then((result: BusinessUnitPrivileges) => {
                setCanProcess(result.canProcess);
                setCanReview(result.canReview);
                setCanGrade(result.canGrade);
                setSupervisor(result.supervisor);
            })
            .catch(error => console.error(error.message));
    }, [fetchDataAuthenticated]);

    useEffect(() => {
        getAuthenticatedAccountPrivilege('AdminPrivilegeName')
            .then(result => setAdmin(result));
        getAuthenticatedAccountPrivilege('CanConfigurePrivilegeName')
            .then(result => setCanConfigure(result));
    }, [getAuthenticatedAccountPrivilege]);

    useEffect(() => {
        if (businessUnitCode) {
            getPrivilegesForBusinessUnit(businessUnitCode);
        }

    }, [businessUnitCode, getPrivilegesForBusinessUnit]);

    useEffect(() => {
        getAuthenticatedAccountBusinessUnitsForPrivilege('CanProcessPrivilegeName')
            .then(result => setBusinessUnitsUserCanProcess(result));

    }, [getAuthenticatedAccountBusinessUnitsForPrivilege]);

    return (
        <PrivilegeContext.Provider value={{ admin, supervisor, canProcess, canReview, canGrade, canConfigure, setBusinessUnitCode, businessUnitsUserCanProcess }}>
            {children}
        </PrivilegeContext.Provider>
    );
}

export default PrivilegeContext;